<template>
  <Grid
    :key="id"
    ref="grid"
    :class="cmpClass"
    :inline="!block"
    @click="e => handleClick(e)"
    @mousedown="e => handleMousedown(e)"
    @mouseenter="e => handleMouseenter(e)"
    @mouseleave="e => handleMouseleave(e)"
    @mouseup="e => handleMouseup(e)"
  >
    <span :class="checkboxClass">
      <span :class="checkboxIndicatorClass">
        <Icon
          v-if="['checked', 'indeterminate'].includes(manualState) || (indeterminate || checked)"
          :icon="(manualState === 'indeterminate' || indeterminate) ? Minus : CheckSmall"
          color="white"
          size="small"
          :stroke-width="8"
        />
      </span>
    </span>
    <input
      ref="checkbox"
      type="checkbox"
      :name="name"
      :value="value"
      :checked="checked"
      :disabled="disabled"
      @focus="e => handleFocus(e)"
      @blur="e => handleBlur(e)"
    >
    <Text
      v-if="label || $slots.default"
      class="d-checkbox-label"
      color="text-title"
      ellipsis
      tooltip
      :disabled="disabled"
    >
      <slot>{{ label }}</slot>
    </Text>
    <template v-if="description || $slots.description">
      <Text
        v-if="expand"
        class="d-checkbox-description"
        color="text-description"
        :disabled="disabled"
      >
        <slot name="description">{{ description }}</slot>
      </Text>
      <Tooltip v-else>
        <Icon
          class="d-checkbox-tooltip-description"
          :icon="Help"
          color="text-description"
        />
        <template #content>
          <slot name="description">{{ description }}</slot>
        </template>
      </Tooltip>
    </template>
  </Grid>
</template>
<script setup lang="ts">
  import {
    ref, reactive, computed, watch, watchEffect, onMounted, onBeforeUnmount, useSlots, withDefaults, inject,
  } from 'vue'
  import { Grid } from '../Grid'
  import { Text } from '../Text'
  import { Tooltip } from '../Popover'
  import { Icon } from '../Icon'
  import { CheckSmall, Minus, Help } from '../../icons'
  import { checkboxGroupProvision, CheckboxGroupProvision } from './provision'
  import { CheckboxContent, CheckboxChild, CheckboxChildren } from './interface'
  import { CheckboxState } from '../../enums'
  import { MouseState } from '../../states'

  const props = withDefaults(
    defineProps<{
      modelValue?:(string | number | boolean)[]
      value?: string | number | boolean
      label?: string
      description?: string
      expand?: boolean
      checked?: boolean
      autofocus?: boolean
      block?: boolean
      indicator?: {
        children: CheckboxChildren
      }
      extra?: any
      id?: string | number
      disabled?: boolean
      manual?: boolean
      manualState?: CheckboxState
    }>(),
    {
      modelValue: undefined,
      value: undefined,
      label: undefined,
      description: undefined,
      expand: undefined,
      checked: false,
      autofocus: false,
      block: false,
      indicator: undefined,
      extra: undefined,
      id: undefined,
      disabled: false,
      manual: false,
      manualState: undefined,
    },
  )

  const emits = defineEmits<{(e: 'update:checked', checked: boolean): void; (e: 'update:modelValue', modelValue: (string | number | boolean)[]): void; (e: 'change', modelValue: (string | number | boolean)[], values: CheckboxContent[]): void; (e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'focus', event: FocusEvent): void; (e: 'blur', event: FocusEvent): void; (e: 'state-change', state: MouseState): void}>()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const modelValue = ref(props.modelValue)

  const children = reactive<CheckboxChildren>(new Map())
  const flattenChildren = ref<CheckboxChildren>(new Map())
  const fullValue = ref<CheckboxContent[]>([])

  const indeterminate = ref(false)

  watch(
    modelValue,
    v => emits('update:modelValue', v),
  )

  watch(
    () => props.modelValue,
    v => {
      modelValue.value = v
      flattenChildren.value
        .forEach(
          child => {
            if (!child.disabled) {
              child
                .changeChecked(
                  !!v?.includes(child.option.value),
                )
            }
          },
        )
    },
  )

  function syncChildIfNotExists(childId: string, child: CheckboxChild) {
    if (!flattenChildren.value.has(childId)) {
      if (modelValue.value?.includes(child.option.value) && !child.checked && !child.disabled) {
        child.changeChecked(true)
      }
    }

    return child
  }

  function flatChildren(v: CheckboxChildren): CheckboxChildren {
    const res: CheckboxChildren = new Map()

    v.forEach(
      (child, childId) => {
        if (child.children.size) {
          const temp = flatChildren(child.children)

          temp
            .forEach(
              (c, i) => res
                .set(
                  i,
                  syncChildIfNotExists(i, c),
                ),
            )
        } else {
          res
            .set(
              childId,
              syncChildIfNotExists(childId, child),
            )
        }
      },
    )

    return res
  }

  const checkboxGroup = inject<CheckboxGroupProvision | undefined>(checkboxGroupProvision, undefined)

  const internalValue = checkboxGroup?.modelValue || ref()

  const name = checkboxGroup?.name
  const id = computed(
    () => `${props.label}-${name?.value}-${props.value}-${props.id}`,
  )
  const disabled = computed(
    () => props.disabled || checkboxGroup?.disabled.value,
  )
  const checked = computed(
    () => !!internalValue.value?.includes(props.value),
  )
  const indicator = computed(
    () => props.indicator || checkboxGroup?.indicator.value,
  )

  function changeInternalValue(v: boolean, fromChildrenChange?: boolean) {
    if (v !== checked.value) {
      if (v) {
        internalValue.value = Array
          .from(
            new Set([...(internalValue.value || []), props.value]),
          )
      } else {
        internalValue.value = internalValue.value?.filter(x => x !== props.value)
      }

      if (!fromChildrenChange) {
        children.forEach(
          child => {
            if (!child.disabled) {
              child.changeChecked(v)
            }
          },
        )
      }
    }
  }

  watch(
    children,
    v => {
      const temp: CheckboxContent[] = []
      let disabledUnchecked: number = 0

      flattenChildren.value = flatChildren(v)

      flattenChildren.value
        .forEach(
          child => {
            if (child.checked) {
              temp.push(child.option)

              modelValue.value = Array
                .from(
                  new Set([...(modelValue.value || []), child.option.value]),
                )
            } else {
              modelValue.value = modelValue.value?.filter(x => x !== child.option.value)

              if (child.disabled) {
                disabledUnchecked += 1
              }
            }
          },
        )

      fullValue.value = temp

      emits('change', modelValue.value, fullValue.value)

      if (flattenChildren.value.size) {
        const allChecked = temp.length === flattenChildren.value.size

        indeterminate.value = !allChecked
          ? temp.length > 0
          : false

        changeInternalValue(temp.length + disabledUnchecked === flattenChildren.value.size, true)
      } else {
        indeterminate.value = false

        changeInternalValue(false, true)
      }

      if (indicator.value) {
        indicator.value
          .children
          .set(
            id.value,
            {
              option: {
                ...props,
                checked: checked.value,
                indicator: indicator.value,
                disabled: disabled.value,
              },
              children,
              checked: checked.value,
              disabled: disabled.value,
              changeChecked: changeInternalValue,
            },
          )
      }
    },
    { deep: true },
  )

  watch(
    [
      checked,
      indicator,
      disabled,
    ],
    ([c, i, d], [oc]) => {
      if (c !== oc) {
        emits('update:checked', c)
      }

      const option = {
        ...props,
        checked: c,
        indicator: i,
        disabled: d,
      }

      if (i) {
        i.children
          .set(
            id.value,
            {
              option,
              children,
              checked: c,
              disabled: d,
              changeChecked: changeInternalValue,
            },
          )
      }

      if (checkboxGroup) {
        checkboxGroup.children
          .set(
            id.value,
            {
              option,
              children,
              checked: c,
              disabled: d,
              changeChecked: changeInternalValue,
            },
          )
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(
    () => {
      indicator.value?.children.delete(id.value)
      checkboxGroup?.children.delete(id.value)
    },
  )

  watch(
    () => props.checked,
    (v, ov) => {
      if (v !== ov) {
        changeInternalValue(v)
      }
    },
    { immediate: props.checked },
  )

  const checkbox = ref()
  const focus = ref(false)

  const expand = computed(
    () => (
      props.expand !== undefined
        ? props.expand
        : checkboxGroup?.expand.value
    ),
  )

  const slots = useSlots()

  const cmpClass = computed(
    () => [
      'd-checkbox',
      `d-checkbox-main${
        (slots.default || props.label)
          ? '-label'
          : ''
      }${
        // eslint-disable-next-line no-nested-ternary
        (slots.description || props.description)
          ? expand.value
            ? '-description'
            : '-tooltip-description'
          : ''
      }`,
      'd-clickable',
      disabled.value && 'disabled',
    ],
  )

  const checkboxClass = computed(
    () => [
      'd-checkbox-simulator',
      '--color-bg-white',
      state.pressing && 'pressing',
      state.hover && 'hover',
      // eslint-disable-next-line no-nested-ternary
      ...(props.manualState === 'checked' || checked.value)
        ? ['checked', '--color-border-primary']
        : (props.manualState === 'indeterminate' || indeterminate.value)
          ? ['indeterminate', '--color-border-primary']
          : ['unchecked'],
      disabled.value && 'disabled',
      focus.value && 'focus',
    ],
  )

  const checkboxIndicatorClass = computed(
    () => [
      'd-checkbox-indicator',
      '--color-bg-white',
      state.pressing && 'pressing',
      state.hover && 'hover',
      disabled.value && 'disabled',
      // eslint-disable-next-line no-nested-ternary
      ...(['checked', 'indeterminate'].includes(props.manualState) || (indeterminate.value || checked.value))
        ? ['--color-bg-primary']
        : [
          disabled.value
            ? '--color-bg-fill'
            : '--color-bg-fill-light',
        ],
    ],
  )

  function handleClick(e: MouseEvent) {
    emits('click', e)
    if (!disabled.value && !props.manual) {
      if (checkboxGroup?.validating.value !== true && checkboxGroup?.validateTiming.value === 'blur') {
        checkboxGroup.validating.value = true
      }

      changeInternalValue(!checked.value)
    }
  }

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  function handleFocus(e: FocusEvent) {
    emits('focus', e)
    focus.value = true
  }

  function handleBlur(e: FocusEvent) {
    emits('blur', e)
    focus.value = false
  }

  if (props.autofocus) {
    onMounted(
      () => checkbox.value.focus(),
    )
  }

  const grid = ref<{ $el: HTMLElement | undefined }>()

  const exposed = reactive({
    $el: grid.value?.$el,
    children,
  })

  watchEffect(
    () => {
      exposed.$el = grid.value?.$el
    },
  )

  defineExpose(exposed)
</script>
